import React from 'react';
import Card from '../../../components/base/Card';

function ShirtsSalesTab({ salesData }) {
  const shirtsData = salesData?.shirts || {
    total: 1890000,
    pieces: 1250,
    growth: 8.3,
    orders: 1250,
    avgOrder: 1512,
    dailyData: [
      { date: '2026-02-01', sales: 32000, orders: 21, pieces: 315 },
      { date: '2026-02-02', sales: 28000, orders: 18, pieces: 270 },
      { date: '2026-02-03', sales: 45000, orders: 30, pieces: 450 },
      { date: '2026-02-04', sales: 38000, orders: 25, pieces: 375 },
      { date: '2026-02-05', sales: 51000, orders: 34, pieces: 510 },
      { date: '2026-02-06', sales: 42000, orders: 28, pieces: 420 },
      { date: '2026-02-07', sales: 35000, orders: 23, pieces: 345 }
    ]
  };

  const shirtsProducts = [
    { name: 'Pure White Cotton Shirt', color: 'White', sales: 650000, units: 45, pieces: 450, growth: 14.1 },
    { name: 'Navy Blue Formal Executive Shirt', color: 'Navy Blue', sales: 520000, units: 35, pieces: 375, growth: 10.5 },
    { name: 'Washed Denim Casual Shirt', color: 'Light Blue', sales: 420000, units: 25, pieces: 240, growth: 6.8 },
    { name: 'Black Formal Microprint Shirt', color: 'Black', sales: 300000, units: 20, pieces: 185, growth: 4.2 }
  ];

  const maxSales = Math.max(...(shirtsData.dailyData?.map(d => d.sales) || [100000]), 1);

  return (
    <div className="space-y-6">
      {/* 4 Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900">₹{shirtsData.total?.toLocaleString()}</p>
              <p className="text-xs text-green-600 font-medium">+{shirtsData.growth}% growth</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-emerald-100 rounded-full">
              <i className="ri-t-shirt-line text-2xl text-emerald-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Pieces Sold</p>
              <p className="text-2xl font-bold text-gray-900">{shirtsData.pieces} Pcs</p>
              <p className="text-xs text-emerald-600">Cotton & Denim Units</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <i className="ri-shopping-bag-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{shirtsData.orders}</p>
              <p className="text-xs text-blue-600">Shirts Category</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-amber-100 rounded-full">
              <i className="ri-calculator-line text-2xl text-amber-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
              <p className="text-xl font-bold text-gray-900">₹{shirtsData.avgOrder?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Per Order Average</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Shirts Daily Trend Chart */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Shirts Sales Trend</h3>
            <p className="text-sm text-gray-500">Daily sales revenue and piece count progression</p>
          </div>
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
            Shirts Category
          </span>
        </div>

        <div className="relative h-64 bg-gray-50 rounded-lg p-4">
          <svg width="100%" height="200" className="overflow-visible">
            {[0, 25, 50, 75, 100].map(y => (
              <line
                key={y}
                x1="40"
                y1={40 + (y * 1.2)}
                x2="95%"
                y2={40 + (y * 1.2)}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            ))}

            {/* Line graph */}
            <polyline
              fill="none"
              stroke="#10B981"
              strokeWidth="3"
              points={shirtsData.dailyData
                ?.map((d, i) => {
                  const x = 50 + i * 110;
                  const y = 180 - ((d.sales / maxSales) * 140);
                  return `${x},${y}`;
                })
                .join(" ")}
            />

            {/* Points */}
            {shirtsData.dailyData?.map((d, i) => {
              const x = 50 + i * 110;
              const y = 180 - ((d.sales / maxSales) * 140);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#059669"
                    className="hover:r-7 cursor-pointer transition-all"
                  >
                    <title>{`${d.date}: ₹${d.sales.toLocaleString()} | ${d.pieces} Pcs`}</title>
                  </circle>
                  <text x={x} y="195" textAnchor="middle" className="text-xs fill-gray-500">
                    {d.date.split("-").slice(1).join("/")}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </Card>

      {/* Top Shirts Products Table */}
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Shirts Variants</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pieces Sold</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shirtsProducts.map((p, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{p.color}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">₹{p.sales.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.pieces} Pcs ({p.units} Lots)</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      +{p.growth}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default ShirtsSalesTab;
